/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { DEFAULT_MINIMUM_VISIBILITY } from './config/constraints';

/**
 * @typedef {{x: number, y: number}} Position
 * @typedef {{width: number, height: number}} Size
 */

/**
 * Ensures the element stays within viewport boundaries
 * @param {Position} position
 * @param {Size} size
 * @param {{width: number, height: number}} minimumVisibility - Minimum dimensions that must remain visible
 * @returns {Position} Constrained position
 */
export const constrainToViewport = (position, size, minimumVisibility = DEFAULT_MINIMUM_VISIBILITY) => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const maxX = viewportWidth - Math.min(minimumVisibility.width, size.width);
  const x = Math.max(0, Math.min(position.x, maxX));

  const maxY = viewportHeight - Math.min(minimumVisibility.height, size.height);
  const y = Math.max(0, Math.min(position.y, maxY));

  return { x, y };
};

/**
 * Calculates the position to center a popup in the viewport
 *
 * @param {Object} size - The size of the popup
 * @param {number} size.width - Width of the popup in pixels
 * @param {number} size.height - Height of the popup in pixels
 * @returns {Object} The centered position with properties:
 *   - x {number} - The x coordinate
 *   - y {number} - The y coordinate
 */
export const calculateCenteredPosition = (size) => {
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  return {
    x: Math.max(0, (viewportWidth - size.width) / 2),
    y: Math.max(0, (viewportHeight - size.height) / 2)
  };
};
